import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import accessToken from '|/helpers/access-token';
import { isReactErrorClaimed } from '|/helpers/react-error-reporting';

declare global {
  interface Window {
    DD_RUM?: typeof datadogRum;
  }
}

const CAR_MERCH_PREFIX = 'car-merch';

/**
 * Extracts all cookies with the car-merch_ prefix
 */
export function getCarMerchCookies(): Record<string, string> {
  const allCookies = Cookies.get() ?? {};
  return Object.entries(allCookies).reduce(
    (acc, [key, value]) => {
      if (key.startsWith(CAR_MERCH_PREFIX)) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, string>
  );
}

// Set car-merch experiment cookies as global context
const carMerchCookies = getCarMerchCookies();
if (Object.keys(carMerchCookies).length > 0) {
  datadogRum.setGlobalContextProperty('carMerchExperiments', carMerchCookies);
}

datadogRum.init({
  applicationId: 'bc7fb6cc-d24a-48c8-9722-f2ce30b35402',
  clientToken: 'pub6bf64308e6574813f395ad9a5c3f2c98',
  env: process.env.NODE_ENV,
  site: 'datadoghq.com',
  service: 'purchase-ui',
  version: process.env.APP_VERSION,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  // browser-rum 7.15 — these default to true since v6; pinned off to keep 5.x behavior
  trackLongTasks: false,
  trackAnonymousUser: false,
  actionNameAttribute: 'data-analytics-id',
  defaultPrivacyLevel: 'mask-user-input',
  trackViewsManually: true,
  beforeSend: (event, context) => {
    try {
      if (event.type === 'resource' && 'xhr' in context) {
        const { xhr = null } = context;
        const correlationIdArray = xhr?.headers?.['correlation-id'] ?? [];
        const correlationId = (correlationIdArray.length && correlationIdArray[0]) || null;
        if (correlationId) {
          event.context = {
            ...event.context,
            correlationId
          };
        }
      }
      if (event.type === 'error' && event.error.source === 'console') {
        const isChatUiError = event.error.handling_stack?.includes('googleapis');
        event.context = {
          ...event.context,
          isChatUiError
        };
      }
      // A React error already reported through react-error-reporting also reaches RUM as the
      // console.error or window error event some library logs for it; keep only the report.
      if (
        event.type === 'error' &&
        event.error.source !== 'custom' &&
        'error' in context &&
        isReactErrorClaimed(context.error)
      ) {
        return false;
      }
      if (
        event.type === 'error' &&
        event.error.fingerprint === 'v11.F22CBEFAF5D7D41601AFCFAE02AB4C0D'
      ) {
        return false;
      }
    } catch (e) {
      console.error(e);
    }
    return true;
  }
});

datadogRum.setUser({
  // must use id since datadog's queryable columns are not dynamic
  id: accessToken().user.user_id,
  browserCookieId: Cookies.get('BrowserCookieId')
});

datadogRum.startSessionReplayRecording();

window.DD_RUM = datadogRum;
